import React, { useMemo, useState } from 'react';
import {
Box,
Paper,
Typography,
TableContainer,
Table,
TableHead,
TableBody,
TableRow,
TableCell,
TableSortLabel,
Alert
} from '@mui/material';
import {
    //BarChart as BarChartIcon,
    GridOn as GridOnIcon
} from '@mui/icons-material';
import { DashboardData } from '../../types';
import {
getDepartmentContentData,
getOccupationContentData,
orderedResultTypes,
orderedTimings,
normalizeField,
recordWeight,
normalizeReason,
UNFILLED_FIELD_LABEL
} from './utils/dataProcessing';

interface DetailedAnalysisTabProps {
data: DashboardData;
}

const DetailedAnalysisTab: React.FC<DetailedAnalysisTabProps> = ({ data }) => {
// 部署ごとの結果データの取得
const departmentContentData = getDepartmentContentData(data);

// 職種ごとの結果データの取得
const occupationContentData = getOccupationContentData(data);

const [sortConfig, setSortConfig] = useState<{ orderBy: 'date' | 'reason'; order: 'asc' | 'desc' }>({
    orderBy: 'date',
    order: 'desc',
});

const getDateValue = (value?: string) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    const time = parsed.getTime();
    return Number.isNaN(time) ? undefined : time;
};

const sortedRawData = useMemo(() => {
    const copied = [...data.raw_data];
    copied.sort((a, b) => {
        if (sortConfig.orderBy === 'date') {
            const fallback = sortConfig.order === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
            const dateA = getDateValue(a.date) ?? fallback;
            const dateB = getDateValue(b.date) ?? fallback;
            return sortConfig.order === 'asc' ? dateA - dateB : dateB - dateA;
        }
        const reasonA = normalizeReason(a.reason);
        const reasonB = normalizeReason(b.reason);
        const comparison = reasonA.localeCompare(reasonB);
        return sortConfig.order === 'asc' ? comparison : -comparison;
    });
    return copied;
}, [data.raw_data, sortConfig]);

const handleSortChange = (key: 'date' | 'reason') => {
    setSortConfig(prev => {
        if (prev.orderBy === key) {
            return { orderBy: key, order: prev.order === 'asc' ? 'desc' : 'asc' };
        }
        return { orderBy: key, order: key === 'date' ? 'desc' : 'asc' };
    });
};

const formatDate = (value?: string) => {
    if (!value) return UNFILLED_FIELD_LABEL;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return UNFILLED_FIELD_LABEL;
    return parsed.toLocaleString('ja-JP');
};

// 部署×タイミング別分析用の関数
const generateDepartmentTimingData = () => {
    const deptTimingData: Record<string, Record<string, Record<string, number>>> = {};

    // 部署の一覧を取得
    const departments = departmentContentData.map(dept => dept.name);

    // 各部署に対して初期化
    departments.forEach(dept => {
    deptTimingData[dept] = {};
    orderedTimings.forEach(timing => {
        deptTimingData[dept][timing] = {};
        orderedResultTypes.forEach(result => {
        deptTimingData[dept][timing][result] = 0;
        });
    });
    });

    // raw_dataから各部署・タイミング・結果の組み合わせごとにカウント
    data.raw_data.forEach(record => {
    const dept = record.department || '未記入';
    const timing = record.timing;
    const result = record.result;

    // 該当するデータがある場合のみカウント
    if (dept && timing && result && deptTimingData[dept] && deptTimingData[dept][timing]) {
        if (!deptTimingData[dept][timing][result]) {
        deptTimingData[dept][timing][result] = 0;
        }
        deptTimingData[dept][timing][result] += recordWeight(record);
    }
    });

    return deptTimingData;
};

// 部署×職種×タイミング別分析用の関数
const generateDeptOccTimingData = () => {
    // 空欄の場合も「未記入」としてリストに含めるように修正
    const departments = Array.from(new Set(
      data.raw_data.map(record => record.department || '未記入')
    ));

    const occupations = Array.from(new Set(
      data.raw_data.map(record => record.occupation || '未記入')
    ));

    // 結果データ構造を初期化
    const resultData: Record<string, Record<string, Record<string, { implemented: number; total: number }> & { 合計: { implemented: number; total: number } }>> = {};

    // 部署別の合計データを追跡するためのオブジェクト
    const deptTotals: Record<string, { implemented: number; total: number }> = {};

    // 各部署の初期化
    departments.forEach(dept => {
    resultData[dept] = {};
    deptTotals[dept] = {
        implemented: 0,
        total: 0
    };

    // 各職種の初期化
    occupations.forEach(occ => {
        resultData[dept][occ] = { 合計: { implemented: 0, total: 0 } };

        // 各タイミングの初期化
        orderedTimings.forEach(timing => {
        resultData[dept][occ][timing] = {
            implemented: 0, // 実施数（アルコールまたは石鹸と流水）
            total: 0        // 合計数
        };
        });

        // 職種ごとの合計行の初期化
        resultData[dept][occ]['合計'] = {
        implemented: 0,
        total: 0
        };
    });
    });

    // raw_dataから集計
    data.raw_data.forEach(record => {
    const dept = record.department || '未記入';
    const occ = record.occupation || '未記入';
    const timing = record.timing;
    const recordResult = record.result;

    // 有効なデータのみ処理
    if (dept && occ && timing && recordResult &&
        resultData[dept] && resultData[dept][occ] && resultData[dept][occ][timing]) {

        // 職種ごとの合計カウントを増加
        const weight = recordWeight(record);
        resultData[dept][occ][timing].total += weight;
        resultData[dept][occ]['合計'].total += weight;

        // 部署合計も更新
        if (deptTotals[dept]) {
        deptTotals[dept].total += weight;
        }

        // アルコールまたは石鹸で実施した場合は実施カウントを増加
        if (recordResult === "アルコールで実施した" || recordResult === "石鹸と流水で実施した") {
        resultData[dept][occ][timing].implemented += weight;
        resultData[dept][occ]['合計'].implemented += weight;

        // 部署合計の実施数も更新
        if (deptTotals[dept]) {
            deptTotals[dept].implemented += weight;
        }
        }
    }
    });

    // 部署のデータに部署合計を追加
    departments.forEach(dept => {
    resultData[dept]['合計'] = {} as Record<string, { implemented: number; total: number }> & { 合計: { implemented: number; total: number } };

    // 各タイミングの部署合計を計算
    orderedTimings.forEach(timing => {
        resultData[dept]['合計'][timing] = {
        implemented: 0,
        total: 0
        };

        // 全職種からこのタイミングの合計を集計
        Object.keys(resultData[dept]).forEach(occ => {
        if (occ !== '合計' && resultData[dept][occ][timing]) {
            resultData[dept]['合計'][timing].implemented += resultData[dept][occ][timing].implemented;
            resultData[dept]['合計'][timing].total += resultData[dept][occ][timing].total;
        }
        });
    });

    // 部署全体の合計
    resultData[dept]['合計']['合計'] = {
        implemented: deptTotals[dept].implemented,
        total: deptTotals[dept].total
    };
    });

    return resultData;
};

const deptTimingData = generateDepartmentTimingData();
const deptOccTimingData = generateDeptOccTimingData();

// 部署のソート関数（文字部分と数字部分を分離してソート）
const sortDepartments = (a: string, b: string) => {
    const nameA = a || '';
    const nameB = b || '';

    // 正規表現で文字部分と数字部分を抽出
    const regexResult1 = nameA.match(/^([A-Za-z]+)(\d+)$/);
    const regexResult2 = nameB.match(/^([A-Za-z]+)(\d+)$/);

    // マッチしなかった場合（通常の文字列）は通常のソート
    if (!regexResult1 || !regexResult2) {
    return nameA.localeCompare(nameB);
    }

    // 文字部分と数字部分を取得
    const [, letterA, numberA] = regexResult1;
    const [, letterB, numberB] = regexResult2;

    // まず文字部分で比較
    const letterCompare = letterA.localeCompare(letterB);
    if (letterCompare !== 0) {
    return letterCompare;
    }

    // 文字部分が同じなら、数字部分を数値として比較
    return parseInt(numberA, 10) - parseInt(numberB, 10);
};

// 部署を名称順にソート
const departmentsBySortOrder = Object.keys(deptOccTimingData).sort(sortDepartments);

return (
    <Box sx={{ width: '100%' }}>
    <Box sx={{ mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <GridOnIcon sx={{ mr: 1 }} /> 観察データ一覧
        </Typography>
        {sortedRawData.length === 0 ? (
            <Alert severity="info">該当するデータがありません。</Alert>
        ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
                <TableHead>
                <TableRow>
                    <TableCell sortDirection={sortConfig.orderBy === 'date' ? sortConfig.order : false}>
                    <TableSortLabel
                        active={sortConfig.orderBy === 'date'}
                        direction={sortConfig.orderBy === 'date' ? sortConfig.order : 'desc'}
                        onClick={() => handleSortChange('date')}
                    >
                        日付
                    </TableSortLabel>
                    </TableCell>
                    <TableCell>病棟/部署</TableCell>
                    <TableCell>職種</TableCell>
                    <TableCell>場面</TableCell>
                    <TableCell>結果</TableCell>
                    <TableCell sortDirection={sortConfig.orderBy === 'reason' ? sortConfig.order : false}>
                    <TableSortLabel
                        active={sortConfig.orderBy === 'reason'}
                        direction={sortConfig.orderBy === 'reason' ? sortConfig.order : 'asc'}
                        onClick={() => handleSortChange('reason')}
                    >
                        理由
                    </TableSortLabel>
                    </TableCell>
                    <TableCell>コメント</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {sortedRawData.map(record => (
                    <TableRow key={record.id}>
                    <TableCell>{formatDate(record.date)}</TableCell>
                    <TableCell>{normalizeField(record.department)}</TableCell>
                    <TableCell>{normalizeField(record.occupation)}</TableCell>
                    <TableCell>{normalizeField(record.timing)}</TableCell>
                    <TableCell>{record.result || UNFILLED_FIELD_LABEL}</TableCell>
                    <TableCell>{normalizeReason(record.reason)}</TableCell>
                    <TableCell>{record.comment?.trim() || UNFILLED_FIELD_LABEL}</TableCell>
                    </TableRow>
                ))}
                </TableBody>
            </Table>
            </TableContainer>
        )}
        </Paper>
    </Box>
    {/* 部署×観察タイミング別分析表 */}
    <Box sx={{ mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <GridOnIcon sx={{ mr: 1 }} /> 部署×観察タイミング
        </Typography>
            <TableContainer sx={{ maxHeight: 650 }}>
            <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                <TableCell rowSpan={2} sx={{ position: 'sticky', top: 0, zIndex: 3 }}>部署</TableCell>
                <TableCell rowSpan={2} sx={{ position: 'sticky', top: 0, zIndex: 3 }}>観察タイミング</TableCell>
                <TableCell colSpan={3} align="center" sx={{ position: 'sticky', top: 0, zIndex: 3 }}>結果</TableCell>
                <TableCell rowSpan={2} sx={{ position: 'sticky', top: 0, zIndex: 3 }} align="center">合計</TableCell>
                <TableCell rowSpan={2} sx={{ position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f5f5f5' }} align="center">遵守率</TableCell>
                </TableRow>
                <TableRow>
                {orderedResultTypes.map(result => (
                    <TableCell
                    key={result}
                    align="center"
                    sx={{
                        position: 'sticky',
                        top: 37, // 1行目のヘッダーの高さに合わせて調整
                        zIndex: 2,
                        backgroundColor: 'white' // 背景色を設定して下の内容を隠す
                    }}
                    >
                    {result}
                    </TableCell>
                ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {departmentContentData.map((deptRow, deptIndex) => {
                const deptName = deptRow.name;

                return (
                    <React.Fragment key={`dept-${deptIndex}`}>
                    {/* 各タイミングの行 - すべてのタイミングを表示 */}
                    {orderedTimings.map((timing, timingIndex) => {
                        // 最初のタイミングのみ部署名を表示
                        const isFirstTiming = timingIndex === 0;

                        // この部署×タイミングの各結果のカウント
                        const resultCounts = orderedResultTypes.map(result =>
                        deptTimingData[deptName]?.[timing]?.[result] || 0
                        );

                        // 合計を計算
                        const total = resultCounts.reduce((sum, count) => sum + count, 0);

                        // 遵守率を計算（アルコール + 石鹸と流水の合計 / 全体）
                        const complianceCount = (
                        (deptTimingData[deptName]?.[timing]?.["アルコールで実施した"] || 0) +
                        (deptTimingData[deptName]?.[timing]?.["石鹸と流水で実施した"] || 0)
                        );

                        // 遵守率 - データがない場合は「--」と表示
                        let complianceRateDisplay = "--";
                        let complianceRateValue = 0;

                        if (total > 0) {
                        complianceRateValue = (complianceCount / total * 100);
                        complianceRateDisplay = complianceRateValue.toFixed(1) + "%";
                        }

                        return (
                        <TableRow key={`dept-${deptIndex}-timing-${timingIndex}`}>
                            {isFirstTiming && (
                            <TableCell rowSpan={orderedTimings.length + 1} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                {deptName}
                            </TableCell>
                            )}
                            <TableCell>{timing}</TableCell>
                            {orderedResultTypes.map((result, resultIndex) => (
                            <TableCell key={`result-${resultIndex}`} align="center">
                                {deptTimingData[deptName]?.[timing]?.[result] || 0}
                            </TableCell>
                            ))}
                            <TableCell align="center">{total}</TableCell>
                            <TableCell align="center" sx={{
                            backgroundColor: '#f5f5f5',
                            fontWeight: 'bold',
                            color: total === 0 ? 'black' :
                                    complianceRateValue >= 80 ? 'green' :
                                    complianceRateValue >= 50 ? 'black' : 'red'
                            }}>
                            {complianceRateDisplay}
                            </TableCell>
                        </TableRow>
                        );
                    })}

                    {/* 部署ごとの合計行 */}
                    <TableRow sx={{ backgroundColor: '#f8f8f8' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>合計</TableCell>

                        {/* 各結果タイプの合計 */}
                        {orderedResultTypes.map((result, resultIndex) => {
                        // この部署の各結果タイプの合計を計算
                        const resultTotal = orderedTimings.reduce((sum, timing) => {
                            return sum + (deptTimingData[deptName]?.[timing]?.[result] || 0);
                        }, 0);

                        return (
                            <TableCell key={`total-result-${resultIndex}`} align="center" sx={{ fontWeight: 'bold' }}>
                            {resultTotal}
                            </TableCell>
                        );
                        })}

                        {/* 部署の総合計と遵守率 */}
                        {(() => {
                        // 部署全体の合計を計算
                        const deptTotal = orderedTimings.reduce((sum, timing) => {
                            return sum + orderedResultTypes.reduce((tSum, result) => {
                            return tSum + (deptTimingData[deptName]?.[timing]?.[result] || 0);
                            }, 0);
                        }, 0);

                        // 部署全体の遵守率を計算
                        const deptComplianceCount = orderedTimings.reduce((sum, timing) => {
                            return sum +
                            (deptTimingData[deptName]?.[timing]?.["アルコールで実施した"] || 0) +
                            (deptTimingData[deptName]?.[timing]?.["石鹸と流水で実施した"] || 0);
                        }, 0);

                        // 遵守率 - データがない場合は「--」と表示
                        let deptComplianceRateDisplay = "--";
                        let deptComplianceRateValue = 0;

                        if (deptTotal > 0) {
                            deptComplianceRateValue = (deptComplianceCount / deptTotal * 100);
                            deptComplianceRateDisplay = deptComplianceRateValue.toFixed(1) + "%";
                        }

                        return (
                            <>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                {deptTotal}
                            </TableCell>
                            <TableCell align="center" sx={{
                                backgroundColor: '#f5f5f5',
                                fontWeight: 'bold',
                                color: deptTotal === 0 ? 'black' :
                                    deptComplianceRateValue >= 80 ? 'green' :
                                    deptComplianceRateValue >= 50 ? 'black' : 'red'
                            }}>
                                {deptComplianceRateDisplay}
                            </TableCell>
                            </>
                        );
                        })()}
                    </TableRow>
                    </React.Fragment>
                );
                })}
            </TableBody>
            </Table>
        </TableContainer>
        </Paper>
    </Box>

    {/* 職種×観察タイミング別分析表 */}
    <Box sx={{ mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <GridOnIcon sx={{ mr: 1 }} /> 職種×観察タイミング
        </Typography>

        {/* 職種ごとのタイミング別データを生成する関数 */}
        {(() => {
            // 職種ごとのタイミング別結果データを集計
            const generateOccupationTimingData = () => {
            const occTimingData: Record<string, Record<string, Record<string, number>>> = {};

            // 職種の一覧を取得
            const occupations = occupationContentData.map(occ => occ.name);

            // 各職種に対して初期化
            occupations.forEach(occ => {
                occTimingData[occ] = {};
                orderedTimings.forEach(timing => {
                occTimingData[occ][timing] = {};
                orderedResultTypes.forEach(result => {
                    occTimingData[occ][timing][result] = 0;
                });
                });
            });

            // raw_dataから各職種・タイミング・結果の組み合わせごとにカウント
            data.raw_data.forEach(record => {
                const occ = record.occupation || '未記入';
                const timing = record.timing;
                const result = record.result;

                // 該当するデータがある場合のみカウント
                if (occ && timing && result && occTimingData[occ] && occTimingData[occ][timing]) {
                if (!occTimingData[occ][timing][result]) {
                    occTimingData[occ][timing][result] = 0;
                }
                occTimingData[occ][timing][result] += recordWeight(record);
                }
            });

            return occTimingData;
            };

            const occTimingData = generateOccupationTimingData();

            return (
            <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                    <TableCell rowSpan={2} sx={{ position: 'sticky', top: 0, zIndex: 3 }}>職種</TableCell>
                    <TableCell rowSpan={2} sx={{ position: 'sticky', top: 0, zIndex: 3 }}>観察タイミング</TableCell>
                    <TableCell colSpan={3} align="center" sx={{ position: 'sticky', top: 0, zIndex: 3 }}>結果</TableCell>
                    <TableCell rowSpan={2} sx={{ position: 'sticky', top: 0, zIndex: 3 }} align="center">合計</TableCell>
                    <TableCell rowSpan={2} sx={{ position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f5f5f5' }} align="center">遵守率</TableCell>
                    </TableRow>
                    <TableRow>
                    {orderedResultTypes.map(result => (
                        <TableCell
                        key={result}
                        align="center"
                        sx={{
                            position: 'sticky',
                            top: 37, // 1行目のヘッダーの高さに合わせて調整
                            zIndex: 2,
                            backgroundColor: 'white' // 背景色を設定して下の内容を隠す
                        }}
                        >
                        {result}
                        </TableCell>
                    ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {occupationContentData.map((occRow, occIndex) => {
                    const occName = occRow.name;

                    return (
                        <React.Fragment key={`occ-${occIndex}`}>
                        {/* 各タイミングの行 - すべてのタイミングを表示 */}
                        {orderedTimings.map((timing, timingIndex) => {
                            // 最初のタイミングのみ職種名を表示
                            const isFirstTiming = timingIndex === 0;

                            // この職種×タイミングの各結果のカウント
                            const resultCounts = orderedResultTypes.map(result =>
                            occTimingData[occName]?.[timing]?.[result] || 0
                            );

                            // 合計を計算
                            const total = resultCounts.reduce((sum, count) => sum + count, 0);

                            // 遵守率を計算（アルコール + 石鹸と流水の合計 / 全体）
                            const complianceCount = (
                            (occTimingData[occName]?.[timing]?.["アルコールで実施した"] || 0) +
                            (occTimingData[occName]?.[timing]?.["石鹸と流水で実施した"] || 0)
                            );

                            // 遵守率 - データがない場合は「--」と表示
                            let complianceRateDisplay = "--";
                            let complianceRateValue = 0;

                            if (total > 0) {
                            complianceRateValue = (complianceCount / total * 100);
                            complianceRateDisplay = complianceRateValue.toFixed(1) + "%";
                            }

                            return (
                            <TableRow key={`occ-${occIndex}-timing-${timingIndex}`}>
                                {isFirstTiming && (
                                <TableCell rowSpan={orderedTimings.length + 1} sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                    {occName}
                                </TableCell>
                                )}
                                <TableCell>{timing}</TableCell>
                                {orderedResultTypes.map((result, resultIndex) => (
                                <TableCell key={`result-${resultIndex}`} align="center">
                                    {occTimingData[occName]?.[timing]?.[result] || 0}
                                </TableCell>
                                ))}
                                <TableCell align="center">{total}</TableCell>
                                <TableCell align="center" sx={{
                                backgroundColor: '#f5f5f5',
                                fontWeight: 'bold',
                                color: total === 0 ? 'black' :
                                        complianceRateValue >= 80 ? 'green' :
                                        complianceRateValue >= 50 ? 'black' : 'red'
                                }}>
                                {complianceRateDisplay}
                                </TableCell>
                            </TableRow>
                            );
                        })}

                        {/* 職種ごとの合計行 */}
                        <TableRow sx={{ backgroundColor: '#f8f8f8' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>合計</TableCell>

                            {/* 各結果タイプの合計 */}
                            {orderedResultTypes.map((result, resultIndex) => {
                            // この職種の各結果タイプの合計を計算
                            const resultTotal = orderedTimings.reduce((sum, timing) => {
                                return sum + (occTimingData[occName]?.[timing]?.[result] || 0);
                            }, 0);

                            return (
                                <TableCell key={`total-result-${resultIndex}`} align="center" sx={{ fontWeight: 'bold' }}>
                                {resultTotal}
                                </TableCell>
                            );
                            })}

                            {/* 職種の総合計と遵守率 */}
                            {(() => {
                            // 職種全体の合計を計算
                            const occTotal = orderedTimings.reduce((sum, timing) => {
                                return sum + orderedResultTypes.reduce((tSum, result) => {
                                return tSum + (occTimingData[occName]?.[timing]?.[result] || 0);
                                }, 0);
                            }, 0);

                            // 職種全体の遵守率を計算
                            const occComplianceCount = orderedTimings.reduce((sum, timing) => {
                                return sum +
                                (occTimingData[occName]?.[timing]?.["アルコールで実施した"] || 0) +
                                (occTimingData[occName]?.[timing]?.["石鹸と流水で実施した"] || 0);
                            }, 0);

                            // 遵守率 - データがない場合は「--」と表示
                            let occComplianceRateDisplay = "--";
                            let occComplianceRateValue = 0;

                            if (occTotal > 0) {
                                occComplianceRateValue = (occComplianceCount / occTotal * 100);
                                occComplianceRateDisplay = occComplianceRateValue.toFixed(1) + "%";
                            }

                            return (
                                <>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {occTotal}
                                </TableCell>
                                <TableCell align="center" sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontWeight: 'bold',
                                    color: occTotal === 0 ? 'black' :
                                        occComplianceRateValue >= 80 ? 'green' :
                                        occComplianceRateValue >= 50 ? 'black' : 'red'
                                }}>
                                    {occComplianceRateDisplay}
                                </TableCell>
                                </>
                            );
                            })()}
                        </TableRow>
                        </React.Fragment>
                    );
                    })}
                </TableBody>
                </Table>
            </TableContainer>
            );
        })()}
        </Paper>
    </Box>

    {/* 部署×職種×観察タイミング別分析表 */}
    <Box sx={{ mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <GridOnIcon sx={{ mr: 1 }} /> 部署×職種×観察タイミング
        </Typography>
        <TableContainer sx={{ maxHeight: 650 }}>
            <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                <TableCell sx={{ position: 'sticky', top: 0, zIndex: 3, width: 100}}>部署</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, zIndex: 3, width: 130}}>職種</TableCell>
                {orderedTimings.map((timing, index) => (
                    <TableCell key={`timing-header-${index}`} align="center"
                    sx={{ position: 'sticky', top: 0, zIndex: 3, minWidth: 80}}>
                    {timing}
                    </TableCell>
                ))}
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 3, minWidth: 80}}>合計</TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 3, width: 80, backgroundColor: '#f5f5f5' }}>順守率</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {departmentsBySortOrder.map((dept, deptIndex) => {
                // 職種を名称順でソート
                const occupations = Object.keys(deptOccTimingData[dept])
                    .filter(occ => occ !== '合計') // 合計行は別途処理
                    .sort((a, b) => a.localeCompare(b)); // 職種名で通常のアルファベット順ソート

                return (
                    <React.Fragment key={`dept-${deptIndex}`}>
                    {occupations.map((occ, occIndex) => {
                        const isFirstOcc = occIndex === 0;

                        // 部署の全職種のデータ行数を計算（+1は合計行のため）
                        const deptRowspan = occupations.length + 1;

                        // この職種の合計行データ
                        const totalData = deptOccTimingData[dept][occ]['合計'];
                        // 順守率
                        const complianceRate = totalData.total === 0
                        ? "--"
                        : (totalData.implemented / totalData.total * 100) === 0
                            ? "0.0%"
                            : (totalData.implemented / totalData.total * 100).toFixed(1) + "%";

                        return (
                        <TableRow key={`dept-${deptIndex}-occ-${occIndex}`}>
                            {isFirstOcc && (
                            <TableCell rowSpan={deptRowspan} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                {dept}
                            </TableCell>
                            )}
                            <TableCell>{occ}</TableCell>

                            {/* 各タイミングの実施・合計データ */}
                            {orderedTimings.map((timing, timingIndex) => {
                            const timingData = deptOccTimingData[dept][occ][timing] || {implemented: 0, total: 0};

                            return (
                                <TableCell key={`dept-${deptIndex}-occ-${occIndex}-timing-${timingIndex}`} align="center">
                                {timingData.implemented}/{timingData.total}
                                </TableCell>
                            );
                            })}

                            {/* 合計列 */}
                            <TableCell align="center">{totalData.implemented}/{totalData.total}</TableCell>

                            {/* 順守率 */}
                            <TableCell align="center" sx={{
                            fontWeight: 'bold',
                            backgroundColor: '#f5f5f5',
                            color: totalData.total === 0 ? 'black' :
                                    (totalData.implemented / totalData.total * 100) < 50 ? 'red' :
                                    (totalData.implemented / totalData.total * 100) >= 80 ? 'green' : 'black'
                            }}>
                            {complianceRate}
                            </TableCell>
                        </TableRow>
                        );
                    })}

                    {/* 部署合計行 */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>合計</TableCell>

                        {/* 各タイミングの合計 */}
                        {orderedTimings.map((timing, timingIndex) => {
                        const deptTimingTotal = deptOccTimingData[dept]['合計'][timing] || {implemented: 0, total: 0};

                        return (
                            <TableCell key={`dept-${deptIndex}-total-timing-${timingIndex}`} align="center" sx={{ fontWeight: 'bold' }}>
                            {deptTimingTotal.implemented}/{deptTimingTotal.total}
                            </TableCell>
                        );
                        })}

                        {/* 部署全体の合計 */}
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {deptOccTimingData[dept]['合計']['合計'].implemented}/{deptOccTimingData[dept]['合計']['合計'].total}
                        </TableCell>

                        {/* 部署全体の遵守率 */}
                        {(() => {
                        const deptTotal = deptOccTimingData[dept]['合計']['合計'];
                        const deptComplianceRate = deptTotal.total === 0
                            ? "--"
                            : (deptTotal.implemented / deptTotal.total * 100) === 0
                            ? "0.0%"
                            : (deptTotal.implemented / deptTotal.total * 100).toFixed(1) + "%";

                        return (
                            <TableCell align="center" sx={{
                            backgroundColor: '#f5f5f5',
                            fontWeight: 'bold',
                            color: deptTotal.total === 0 ? 'black' :
                                    (deptTotal.implemented / deptTotal.total * 100) < 50 ? 'red' :
                                    (deptTotal.implemented / deptTotal.total * 100) >= 80 ? 'green' : 'black'
                            }}>
                            {deptComplianceRate}
                            </TableCell>
                        );
                        })()}
                    </TableRow>
                    </React.Fragment>
                );
                })}
            </TableBody>
            </Table>
        </TableContainer>
        </Paper>
    </Box>
    </Box>
);
};

export default DetailedAnalysisTab;
